import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { assignmentService, workflowService, dashboardService, widgetService } from '../services';
import { widgetRegistry } from '../components/widgets/registry';
import * as widgetRefetchRegistry from '../components/widgets/blocks/hooks/widgetRefetchRegistry';
import {
  Workflow,
  WorkflowAssignment,
  Dashboard,
  WidgetConfig,
  SpecificWidgetConfig,
  TaskProgress,
  QADataContent,
  ConvictionDataContent,
  ImageRepositoryDataContent,
  PDFRepositoryDataContent,
  NotesRepositoryDataContent,
  ScorecardDataContent,
  ExcelDataContent,
  WidgetTemplate,
  WidgetType,
  DashboardWidgetInstance,
  StepWidgetConfigs,
  WidgetInstanceConfig,
  DashboardSnapshot,
  WorkflowSnapshot,
} from '../types';
import { GridLayoutConfig, GridItemPosition, buildGridFromWidgets } from '../types/gridLayout';
import { isGridLayoutConfig, migrateFlexLayoutToGrid } from '../utils/layoutMigration';
import { WidgetRenderer } from '../components/widgets';
import { isInputWidget } from '../components/widgets/registry';
import { FALLBACK_WIDGET_ICON, CHECK_ICON, WARNING_ICON, CLIPBOARD_ICON, EDIT_ICON } from '../components/widgets/widgetIcons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faCircle, faTriangleExclamation, faChartBar, faPenToSquare, faClipboard, faChevronDown, faSearch } from '@fortawesome/free-solid-svg-icons';
import DashboardGrid from '../components/dashboard/DashboardGrid';
import {
  validateStepWidgets,
  StepValidationResult,
  getValidationSummary,
} from '../utils/widgetValidation';
import ConfirmationModal from '../components/common/ConfirmationModal';



interface StepData {
  id: string;
  name: string;
  description?: string;
  dashboardId?: string;
  // Widget configurations from workflow step (Q&A questions, Excel files, etc.)
  widgetConfigs?: StepWidgetConfigs;
  status: 'completed' | 'in_progress' | 'pending';
  // IDs of step nodes that must be completed before this step is accessible
  dependsOn: string[];
}

const TaskExecution: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';

  const [assignment, setAssignment] = useState<WorkflowAssignment | null>(null);
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [steps, setSteps] = useState<StepData[]>([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);

  // Dashboard and widget state
  const [currentDashboard, setCurrentDashboard] = useState<Dashboard | null>(null);
  const [widgetConfigs, setWidgetConfigs] = useState<WidgetConfig[]>([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [isDashboardFullscreen, setIsDashboardFullscreen] = useState(true);
  const [toolbarPos, setToolbarPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const toolbarDragRef = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const [dashboardData, setDashboardData] = useState<Record<string, unknown>>({});
  const [gridLayoutConfig, setGridLayoutConfig] = useState<GridLayoutConfig | null>(null);
  const [widgetTemplates, setWidgetTemplates] = useState<Map<string, WidgetTemplate>>(new Map());
  const [dashboardWidgets, setDashboardWidgets] = useState<DashboardWidgetInstance[]>([]);
  // Dashboard snapshots from workflow version - used to ensure workers see
  // the same dashboard structure they were assigned, even if dashboards change later
  const [dashboardSnapshots, setDashboardSnapshots] = useState<Record<string, DashboardSnapshot>>(
    {},
  );
  // Progress data map - stores TaskProgress per step with widget data
  // This is the assignment-scoped data store (not the dashboard)
  const [progressDataMap, setProgressDataMap] = useState<Map<string, TaskProgress>>(new Map());
  // Ref mirror so useEffects can read progressDataMap without re-triggering
  const progressDataMapRef = useRef<Map<string, TaskProgress>>(progressDataMap);
  progressDataMapRef.current = progressDataMap;
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // Sibling assignments for asset/workflow switching
  const [sameWorkflowAssignments, setSameWorkflowAssignments] = useState<WorkflowAssignment[]>([]);
  const [sameAssetAssignments, setSameAssetAssignments] = useState<WorkflowAssignment[]>([]);
  const [showAssetSwitcher, setShowAssetSwitcher] = useState(false);
  const [showWorkflowSwitcher, setShowWorkflowSwitcher] = useState(false);
  const [assetSearchQuery, setAssetSearchQuery] = useState('');
  const [workflowSearchQuery, setWorkflowSearchQuery] = useState('');
  const [showStepPanel, setShowStepPanel] = useState(false);
  const stepPanelRef = useRef<HTMLDivElement>(null);
  const assetSwitcherRef = useRef<HTMLDivElement>(null);
  const workflowSwitcherRef = useRef<HTMLDivElement>(null);

  // Load assignment and workflow data
  useEffect(() => {
    const loadData = async () => {
      if (!assignmentId) return;

      try {
        setLoading(true);
        setError(null);

        // Load assignment
        const assignmentData = await assignmentService.getAssignment(assignmentId);
        setAssignment(assignmentData);

        // Use workflow snapshot if available (Lock on Assignment - MVP versioning)
        // This ensures workers see the same workflow config they were assigned,
        // regardless of any subsequent changes to the workflow
        let workflowData: Workflow;
        if (assignmentData.workflowSnapshot) {
          workflowData = {
            id: assignmentData.workflowId,
            name: assignmentData.workflowSnapshot.name,
            description: assignmentData.workflowSnapshot.description,
            flowData: assignmentData.workflowSnapshot.flowData,
            status: 'published',
            requiresAsset: true, // Default for snapshots (field didn't exist before)
            tags: [], // Snapshots predate the unified tag system
            ownerId: '',
            createdAt: assignmentData.assignedAt,
            updatedAt: assignmentData.assignedAt,
          };
          // Store dashboard snapshots if available - workers will see the same
          // dashboard widget structure they were assigned, even if dashboards change
          if (assignmentData.workflowSnapshot.dashboardSnapshots) {
            setDashboardSnapshots(assignmentData.workflowSnapshot.dashboardSnapshots);
          }
        } else {
          // Fallback to loading current workflow (for assignments created before snapshot feature)
          workflowData = await workflowService.getWorkflow(assignmentData.workflowId);
        }
        setWorkflow(workflowData);

        // Load existing progress for this assignment
        let progressData: TaskProgress[] = [];
        try {
          progressData = await assignmentService.getProgress(assignmentId);
        } catch {
          // No existing progress found - this is expected for new assignments
        }

        // Create a map of step progress by step_id
        const progressMap = new Map<string, TaskProgress>();
        for (const p of progressData) {
          progressMap.set(p.stepId, p);
        }
        // Store in state for use by dashboard loading
        setProgressDataMap(progressMap);

        // Extract steps from workflow flowData nodes
        if (workflowData.flowData?.nodes) {
          const flowNodes = workflowData.flowData.nodes as any[];
          const flowEdges = (workflowData.flowData.edges || []) as any[];

          // Build a map of node id to node for quick lookup
          const nodeMap = new Map<string, any>();
          flowNodes.forEach((node: any) => nodeMap.set(node.id, node));

          // Build adjacency lists from edges (supports parallel branches)
          const forwardAdj = new Map<string, string[]>();
          const reverseAdj = new Map<string, string[]>();
          flowEdges.forEach((edge: any) => {
            const fwd = forwardAdj.get(edge.source) || [];
            fwd.push(edge.target);
            forwardAdj.set(edge.source, fwd);

            const rev = reverseAdj.get(edge.target) || [];
            rev.push(edge.source);
            reverseAdj.set(edge.target, rev);
          });

          const isStepNode = (nodeId: string) => {
            const n = nodeMap.get(nodeId);
            return n && nodeId !== 'start' && n.type !== 'input' && n.type !== 'output';
          };

          // BFS from 'start' to collect all step nodes in topological order
          const orderedStepNodes: any[] = [];
          const stepDependencies = new Map<string, string[]>();
          const queue: string[] = ['start'];
          const visited = new Set<string>();

          while (queue.length > 0) {
            const curId = queue.shift()!;
            if (visited.has(curId)) continue;
            visited.add(curId);

            if (isStepNode(curId)) {
              orderedStepNodes.push(nodeMap.get(curId));
              // Dependencies = predecessor step nodes (skip start/end nodes)
              const preds = (reverseAdj.get(curId) || []).filter(pid => isStepNode(pid));
              stepDependencies.set(curId, preds);
            }

            (forwardAdj.get(curId) || []).forEach(childId => {
              if (!visited.has(childId)) queue.push(childId);
            });
          }

          // Map to step data with progress
          const stepNodes = orderedStepNodes.map((node: any, index: number) => {
            // Check if there's saved progress for this step
            const existingProgress = progressMap.get(node.id);
            const deps = stepDependencies.get(node.id) || [];
            let status: StepData['status'] = 'pending';

            if (existingProgress) {
              status = existingProgress.status as StepData['status'];
            } else if (deps.length === 0) {
              // Steps with no dependencies start as in_progress (parallel-safe)
              status = 'in_progress';
            }

            return {
              id: node.id,
              name: node.data?.label || `Step ${index + 1}`,
              description: node.data?.description || '',
              dashboardId: node.data?.dashboardId,
              // Widget configurations from workflow step
              widgetConfigs: node.data?.widgetConfigs as StepWidgetConfigs | undefined,
              status,
              dependsOn: deps,
            };
          });

          setSteps(stepNodes);

          // Check for ?step=nodeId query param to jump to a specific step
          const stepParam = searchParams.get('step');
          if (stepParam) {
            const targetIndex = stepNodes.findIndex(s => s.id === stepParam);
            if (targetIndex >= 0) {
              setCurrentStepIndex(targetIndex);
            }
          } else {
            // Find the first non-completed step to set as current
            const firstIncompleteIndex = stepNodes.findIndex(s => s.status !== 'completed');
            if (firstIncompleteIndex > 0) {
              setCurrentStepIndex(firstIncompleteIndex);
            }
          }
        }

        // Update assignment status to in_progress if it's pending
        if (assignmentData.status === 'pending') {
          await assignmentService.updateAssignmentStatus(assignmentId, 'in_progress');
        }
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to load task data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [assignmentId]);

  // Load sibling assignments for asset/workflow switching
  useEffect(() => {
    if (!assignment) return;

    const loadSiblings = async () => {
      try {
        // Get other assignments with same workflow (different assets)
        if (assignment.workflowId) {
          const resp = await assignmentService.getAssignments(1, 50, { workflowId: assignment.workflowId });
          setSameWorkflowAssignments(resp.data.filter(a => a.id !== assignment.id && a.asset));
        }
        // Get other assignments with same asset (different workflows)
        if (assignment.assetId) {
          const resp = await assignmentService.getAssignments(1, 50, { assetId: assignment.assetId });
          setSameAssetAssignments(resp.data.filter(a => a.id !== assignment.id && a.workflow));
        }
      } catch {
        // Non-critical — silently ignore
      }
    };

    loadSiblings();
  }, [assignment?.id]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (assetSwitcherRef.current && !assetSwitcherRef.current.contains(e.target as Node)) {
        setShowAssetSwitcher(false);
      }
      if (workflowSwitcherRef.current && !workflowSwitcherRef.current.contains(e.target as Node)) {
        setShowWorkflowSwitcher(false);
      }
      if (stepPanelRef.current && !stepPanelRef.current.contains(e.target as Node)) {
        setShowStepPanel(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentStep = steps[currentStepIndex];

  // Summary widgets resolved across all steps (loaded once after steps are set)
  interface SummaryWidgetEntry {
    widgetId: string;
    title: string;
    widgetType: WidgetType | '';
    dataKey: string;
    stepId: string;
  }
  const [summaryWidgets, setSummaryWidgets] = useState<SummaryWidgetEntry[]>([]);

  // Load summary widgets from all steps — uses snapshots when available, falls back to live dashboards
  useEffect(() => {
    if (steps.length === 0) return;

    const load = async () => {
      const results: SummaryWidgetEntry[] = [];
      // Collect unique dashboard IDs across all steps
      const dashboardIds = new Set<string>();
      for (const step of steps) {
        if (step.dashboardId) dashboardIds.add(step.dashboardId);
      }

      // Resolve widgets per dashboard — prefer snapshot, fall back to live fetch
      const dashboardWidgetsMap = new Map<string, DashboardWidgetInstance[]>();
      const ids = Array.from(dashboardIds);
      for (const did of ids) {
        if (dashboardSnapshots[did]?.widgets) {
          dashboardWidgetsMap.set(did, dashboardSnapshots[did].widgets);
        } else {
          try {
            const dash = await dashboardService.getDashboard(did);
            dashboardWidgetsMap.set(did, dash.widgets || []);
          } catch { /* skip */ }
        }
      }

      // Collect template IDs for summary widgets and load their types
      const templateIds = new Set<string>();
      for (const widgets of Array.from(dashboardWidgetsMap.values())) {
        for (const w of widgets) {
          if (w.isSummary) templateIds.add(w.templateId);
        }
      }

      const typeMap = new Map<string, WidgetType>();
      const tids = Array.from(templateIds);
      for (const tid of tids) {
        try {
          const tmpl = await widgetService.getWidgetTemplate(tid);
          typeMap.set(tid, tmpl.type);
        } catch { /* skip */ }
      }

      // Build results
      for (const step of steps) {
        if (!step.dashboardId) continue;
        const widgets = dashboardWidgetsMap.get(step.dashboardId);
        if (!widgets) continue;
        for (const w of widgets) {
          if (!w.isSummary) continue;
          const stepWidgetConfig = step.widgetConfigs?.widgets?.[w.id];
          const dataKey =
            (stepWidgetConfig?.dataKey as string) ||
            (w.configOverrides as Record<string, unknown>)?.dataKey as string ||
            w.id;
          results.push({
            widgetId: w.id,
            title: w.title || typeMap.get(w.templateId) || 'Summary',
            widgetType: typeMap.get(w.templateId) || '',
            dataKey,
            stepId: step.id,
          });
        }
      }

      setSummaryWidgets(results);
    };
    load();
  }, [steps, dashboardSnapshots]);

  // Get summary widget data from progress or current dashboard data
  const getSummaryData = useCallback((sw: { dataKey: string; stepId: string }) => {
    // For the current step, use live dashboardData (includes unsaved changes)
    if (sw.stepId === currentStep?.id) {
      return dashboardData[sw.dataKey];
    }
    const progress = progressDataMap.get(sw.stepId);
    if (!progress?.data) return undefined;
    return progress.data[sw.dataKey];
  }, [progressDataMap, currentStep?.id, dashboardData]);

  // Load dashboard when current step changes
  useEffect(() => {
    const loadDashboard = async () => {
      if (!currentStep?.dashboardId) {
        setCurrentDashboard(null);
        setWidgetConfigs([]);
        setDashboardError(null);
        setDashboardData({});
        setGridLayoutConfig(null);
        setWidgetTemplates(new Map());
        setDashboardWidgets([]);
        return;
      }

      try {
        setDashboardLoading(true);
        setDashboardError(null);

        // Check if we have a snapshot for this dashboard from the workflow version
        // This ensures workers see the same dashboard structure they were assigned
        const dashboardSnapshot = dashboardSnapshots[currentStep.dashboardId];
        let dashboard: Dashboard;

        // Always fetch the live dashboard so widget config data (Q&A questions,
        // conviction categories, etc.) reflects the latest dashboard edits
        const liveDashboard = await dashboardService.getDashboard(currentStep.dashboardId);

        // Use the live dashboard so edits (updated questions, added/removed widgets)
        // are reflected immediately without needing to re-assign the workflow
        dashboard = liveDashboard;

        setCurrentDashboard(dashboard);

        // Initialize widget data from TaskProgress (per-assignment, per-step data)
        const stepProgress = progressDataMapRef.current.get(currentStep.id);
        const savedStepData = stepProgress?.data || {};
        const initialData: Record<string, unknown> = { ...savedStepData };

        // Pass asset's data_uid to widgets for data source filtering
        // This allows widgets to filter external data by the assigned asset
        if (assignment?.asset?.dataUid) {
          initialData.assetDataUid = assignment.asset.dataUid;
        }

        // Dual-read strategy: prefer dashboard-level config data, fall back to legacy step-level widgetConfigs
        const dashboardConfigData = dashboard.data || {};
        const stepConfigs = currentStep.widgetConfigs;

        // Helper: get dataKey for a widget instance
        const getWidgetDataKey = (w: DashboardWidgetInstance): string => {
          const dk = w.configOverrides?.dataKey;
          return typeof dk === 'string' && dk ? dk : w.id;
        };

        // Initialize data from dashboard-level config (new architecture)
        // Dashboard.data contains widget config keyed by dataKey (e.g., Q&A questions, conviction categories)
        // Always load dashboard config even if saved progress exists — the merge loop below
        // will reconcile (e.g., sync updated question lists while preserving saved answers)
        for (const widgetInstance of dashboard.widgets || []) {
          if (widgetInstance.dataSourceConfig?.type === 'dashboard_reference') continue;
          const dataKey = getWidgetDataKey(widgetInstance);
          const dashConfig = dashboardConfigData[dataKey] as Record<string, unknown> | undefined;
          if (!dashConfig) continue;

          // Copy dashboard config as initial data for this widget
          initialData[dataKey] = { ...dashConfig };
        }

        // Legacy: Initialize data from workflow step-level widgetConfigs (backwards compatibility)
        // This handles old workflows where config was stored on the workflow node
        if (stepConfigs?.widgets) {
          for (const [, widgetConfig] of Object.entries(stepConfigs.widgets)) {
            if (!widgetConfig.dataKey) continue;
            // Skip if already initialized from dashboard data or saved progress
            if (initialData[widgetConfig.dataKey]) continue;

            switch (widgetConfig.widgetType) {
              case 'qa':
                initialData[widgetConfig.dataKey] = {
                  title: widgetConfig.title || '',
                  description: widgetConfig.description || '',
                  items: widgetConfig.items || [],
                } as QADataContent;
                break;
              case 'conviction':
                initialData[widgetConfig.dataKey] = {
                  title: widgetConfig.title || '',
                  description: widgetConfig.description || '',
                  categories: widgetConfig.categories || [],
                } as ConvictionDataContent;
                break;
              case 'image_repository':
                initialData[widgetConfig.dataKey] = {
                  title: widgetConfig.title || '',
                  description: widgetConfig.description || '',
                  images: [],
                } as ImageRepositoryDataContent;
                break;
              case 'pdf_repository':
                initialData[widgetConfig.dataKey] = {
                  title: widgetConfig.title || '',
                  description: widgetConfig.description || '',
                  pdfs: [],
                } as PDFRepositoryDataContent;
                break;
              case 'notes_repository':
                initialData[widgetConfig.dataKey] = {
                  title: widgetConfig.title || '',
                  description: widgetConfig.description || '',
                  notes: [],
                } as NotesRepositoryDataContent;
                break;
              case 'scorecard':
                initialData[widgetConfig.dataKey] = {} as ScorecardDataContent;
                break;
            }
          }
        }

        // Merge saved progress answers/scores into template data
        // This preserves user input while respecting the template structure
        for (const widgetInstance of dashboard.widgets || []) {
          if (widgetInstance.dataSourceConfig?.type === 'dashboard_reference') continue;
          const dataKey = getWidgetDataKey(widgetInstance);
          const templateData = initialData[dataKey] as Record<string, unknown> | undefined;
          const savedData = savedStepData[dataKey] as Record<string, unknown> | undefined;

          if (!templateData || !savedData) continue;

          // Merge saved answers into QA items
          if ((templateData as any)?.items && (savedData as any)?.items) {
            const qaTemplate = templateData as unknown as QADataContent;
            const qaSaved = savedData as unknown as QADataContent;
            const savedAnswerMap = new Map<string, string>();
            (qaSaved.items || []).forEach(item => {
              if (item.id && item.answer) savedAnswerMap.set(item.id, item.answer);
            });
            if (qaTemplate.items && qaTemplate.items.length > 0) {
              (templateData as any).items = qaTemplate.items.map(item => ({
                ...item,
                answer: savedAnswerMap.get(item.id) ?? item.answer,
              }));
            } else if (qaSaved.items && qaSaved.items.length > 0) {
              (templateData as any).items = qaSaved.items;
            }
          }

          // Merge saved scores into Conviction categories
          if ((templateData as any)?.categories && (savedData as any)?.categories) {
            const convTemplate = templateData as unknown as ConvictionDataContent;
            const convSaved = savedData as unknown as ConvictionDataContent;
            const savedScoreMap = new Map<string, { score?: number; comment?: string }>();
            (convSaved.categories || []).forEach(cat => {
              if (cat.id) savedScoreMap.set(cat.id, { score: cat.score, comment: cat.comment });
            });
            if (convTemplate.categories && convTemplate.categories.length > 0) {
              (templateData as any).categories = convTemplate.categories.map(cat => ({
                ...cat,
                score: savedScoreMap.get(cat.id)?.score ?? cat.score,
                comment: savedScoreMap.get(cat.id)?.comment ?? cat.comment,
              }));
            } else if (convSaved.categories && convSaved.categories.length > 0) {
              (templateData as any).categories = convSaved.categories;
            }
            if (convSaved.overallScore !== undefined) (templateData as any).overallScore = convSaved.overallScore;
            if (convSaved.overallComment !== undefined) (templateData as any).overallComment = convSaved.overallComment;
          }
        }

        // Note: setDashboardData is called AFTER the widget loop below
        // so that auto-generated dataKeys for unconfigured widgets are included

        // Load widget templates for each widget in the dashboard
        const configs: WidgetConfig[] = [];
        const missingTemplates: string[] = [];
        const templatesMap = new Map<string, WidgetTemplate>();
        const validWidgets: DashboardWidgetInstance[] = [];

        if (dashboard.widgets && dashboard.widgets.length > 0) {
          for (const widgetInstance of dashboard.widgets) {
            try {
              const template = await widgetService.getWidgetTemplate(widgetInstance.templateId);
              templatesMap.set(template.id, template);
              validWidgets.push(widgetInstance as DashboardWidgetInstance);

              // Start with template defaults and dashboard-level overrides
              let mergedConfig = {
                ...template.defaultConfig,
                ...(widgetInstance.configOverrides || {}),
              } as SpecificWidgetConfig;

              // For dashboard_reference widgets, the config comes from dataSourceConfig
              // and the widget type should be 'dashboard_reference', not the source widget's type
              const isDashboardReference =
                widgetInstance.dataSourceConfig?.type === 'dashboard_reference';
              if (isDashboardReference) {
                const dsc = widgetInstance.dataSourceConfig!;
                mergedConfig = {
                  ...mergedConfig,
                  sourceDashboardId: dsc.sourceDashboardId,
                  sourceDataKey: dsc.sourceDataKey,
                  sourceWidgetType: dsc.sourceWidgetType,
                  readOnly: dsc.readOnly ?? true,
                  displayTitle: dsc.displayTitle || widgetInstance.title,
                } as SpecificWidgetConfig;
              }

              // Widget types that require a dataKey for storing user input
              const CONFIGURABLE_WIDGET_TYPES = [
                'qa',
                'conviction',
                'scorecard',
                'image_repository',
                'pdf_repository',
                'notes_repository',
                'onedrive',
              ];

              // Apply dataKey: prefer dashboard-level configOverrides.dataKey, fall back to step-level
              const dashDataKey = getWidgetDataKey(widgetInstance);
              if (dashDataKey && dashDataKey !== widgetInstance.id) {
                // Dashboard has a proper dataKey in configOverrides
                (mergedConfig as any).dataKey = dashDataKey;
              } else if (stepConfigs?.widgets?.[widgetInstance.id]?.dataKey) {
                // Legacy: use step-level dataKey
                (mergedConfig as any).dataKey = stepConfigs.widgets[widgetInstance.id].dataKey;
              }

              // Apply onedrive/excel config: prefer dashboard-level configOverrides, fall back to step-level
              if (template.type === 'onedrive') {
                const overrides = widgetInstance.configOverrides || {};
                const stepConfig = stepConfigs?.widgets?.[widgetInstance.id];
                const fileId = (overrides.fileId as string) || stepConfig?.fileId;
                const fileName = (overrides.fileName as string) || stepConfig?.fileName;
                const fileUrl = (overrides.fileUrl as string) || stepConfig?.fileUrl;
                if (fileId) (mergedConfig as any).fileId = fileId;
                if (fileName) (mergedConfig as any).fileName = fileName;
                if (fileUrl) (mergedConfig as any).fileUrl = fileUrl;
              }

              // Auto-generate dataKey for configurable widgets that don't have one
              // This handles widgets added to dashboard after workflow was configured
              if (
                CONFIGURABLE_WIDGET_TYPES.includes(template.type) &&
                !(mergedConfig as any).dataKey
              ) {
                const autoDataKey = `auto_${widgetInstance.id}`;
                (mergedConfig as any).dataKey = autoDataKey;

                // Initialize data for this widget if not already present
                if (!initialData[autoDataKey]) {
                  switch (template.type) {
                    case 'qa':
                      initialData[autoDataKey] = {
                        title: widgetInstance.title || '',
                        description: '',
                        items: [],
                      } as QADataContent;
                      break;
                    case 'conviction':
                      initialData[autoDataKey] = {
                        title: widgetInstance.title || '',
                        description: '',
                        categories: [],
                      } as ConvictionDataContent;
                      break;
                    case 'image_repository':
                      initialData[autoDataKey] = {
                        title: widgetInstance.title || '',
                        description: '',
                        images: [],
                      } as ImageRepositoryDataContent;
                      break;
                    case 'pdf_repository':
                      initialData[autoDataKey] = {
                        title: widgetInstance.title || '',
                        description: '',
                        pdfs: [],
                      } as PDFRepositoryDataContent;
                      break;
                    case 'notes_repository':
                      initialData[autoDataKey] = {
                        title: widgetInstance.title || '',
                        description: '',
                        notes: [],
                      } as NotesRepositoryDataContent;
                      break;
                    case 'scorecard':
                      initialData[autoDataKey] = {} as ScorecardDataContent;
                      break;
                  }
                }
              }

              // Legacy: Check if widget uses workflow data source from assignment
              if (
                widgetInstance.dataSourceConfig?.source === 'workflow' &&
                widgetInstance.dataSourceConfig.workflowDataKey
              ) {
                const workflowDataKey = widgetInstance.dataSourceConfig.workflowDataKey;
                const workflowWidgetData = assignment?.widgetData?.[workflowDataKey];

                if (workflowWidgetData) {
                  mergedConfig = {
                    ...mergedConfig,
                    ...workflowWidgetData,
                  } as SpecificWidgetConfig;
                }
              }

              // Use 'dashboard_reference' type for reference widgets, otherwise use the template type
              const widgetType = isDashboardReference ? 'dashboard_reference' : template.type;
              configs.push({
                type: widgetType,
                title: widgetInstance.title || template.name,
                config: mergedConfig,
              });
            } catch {
              missingTemplates.push(widgetInstance.title || widgetInstance.templateId);
            }
          }
        }

        if (missingTemplates.length > 0 && configs.length === 0) {
          // All templates are missing - show error
          setDashboardError(
            `Widget templates not found: ${missingTemplates.join(', ')}. The workflow owner needs to edit the dashboard and add new widgets.`,
          );
        }

        // Set dashboard data AFTER widget loop to include any auto-generated dataKeys
        setDashboardData(initialData);

        setWidgetConfigs(configs);
        setWidgetTemplates(templatesMap);
        setDashboardWidgets(validWidgets);

        // Create grid layout config from saved layout
        // Workers get a completely static layout (isEditable=false on DashboardGrid)
        const validWidgetIds = validWidgets.map(w => w.id);


        if (dashboard.layoutConfig && Object.keys(dashboard.layoutConfig).length > 0) {
          try {
            if (isGridLayoutConfig(dashboard.layoutConfig)) {
              // New grid format — filter for valid widgets
              const config = dashboard.layoutConfig as unknown as GridLayoutConfig;
              const validIds = new Set(validWidgetIds);
              const tabGroupIds = new Set<string>();
              const filteredTabGroups = config.tabGroups
                .map(g => ({ ...g, widgetIds: g.widgetIds.filter(id => validIds.has(id)) }))
                .filter(g => g.widgetIds.length > 0);
              filteredTabGroups.forEach(g => tabGroupIds.add(g.id));
              const filteredLayout = config.layout.filter(item => validIds.has(item.i) || tabGroupIds.has(item.i));
              setGridLayoutConfig({
                ...config,
                layout: filteredLayout,
                tabGroups: filteredTabGroups,
              });
            } else {
              // Legacy FlexLayout — migrate
              setGridLayoutConfig(migrateFlexLayoutToGrid(dashboard.layoutConfig, validWidgetIds));
            }
          } catch {
            setGridLayoutConfig(buildGridFromWidgets(validWidgetIds));
          }
        } else if (validWidgetIds.length > 0) {
          setGridLayoutConfig(buildGridFromWidgets(validWidgetIds));
        } else {
          setGridLayoutConfig(null);
        }
      } catch (err: any) {
        setDashboardError(err.response?.data?.detail || 'Failed to load dashboard');
      } finally {
        setDashboardLoading(false);
      }
    };

    loadDashboard();
  }, [
    currentStep?.dashboardId,
    currentStep?.widgetConfigs,
    currentStep?.id,
    assignment,
    dashboardSnapshots,
  ]);

  // Check if all steps are completed
  const allStepsCompleted = steps.length > 0 && steps.every(s => s.status === 'completed');

  // Workflow is locked (read-only) once the assignment has been submitted/completed
  // Also locked while a reopen request is pending approval
  const isWorkflowLocked = assignment?.status === 'completed' || assignment?.status === 'reopen_requested';
  const isReopenRequested = assignment?.status === 'reopen_requested';

  // Check if a step's dependencies are all completed (DAG-aware)
  const areDependenciesMet = useCallback((step: StepData) => {
    return step.dependsOn.every(depId => {
      const depStep = steps.find(s => s.id === depId);
      return depStep?.status === 'completed';
    });
  }, [steps]);

  // Check if current step can be completed:
  // 1. Step is not already completed
  // 2. All dependency steps in the DAG are completed
  const canCompleteCurrentStep =
    currentStep && currentStep.status !== 'completed' && areDependenciesMet(currentStep);

  const completeStep = async () => {
    if (!assignmentId || !currentStep) return;

    // Validate all required fields before completing
    const validation = validateStepWidgets(
      dashboardData,
      dashboardWidgets,
      widgetTemplates,
      currentStep.widgetConfigs,
    );

    if (!validation.isValid) {
      setValidationResult(validation);
      setShowValidationModal(true);
      return;
    }

    try {
      setCompleting(true);

      // Save any unsaved widget data to TaskProgress first
      if (hasUnsavedChanges && assignmentId && currentStep.id) {
        await assignmentService.updateStepProgress(assignmentId, currentStep.id, {
          data: dashboardData,
        });
        setHasUnsavedChanges(false);
      }

      // Call API to complete the step and persist progress
      await assignmentService.completeStep(assignmentId, currentStep.id);

      // Update local state - mark current step as completed
      const updatedSteps = steps.map((s, i) =>
        i === currentStepIndex ? { ...s, status: 'completed' as const } : s,
      );

      // Activate all pending steps whose dependencies are now fully met
      for (let i = 0; i < updatedSteps.length; i++) {
        if (updatedSteps[i].status === 'pending') {
          const allDepsMet = updatedSteps[i].dependsOn.every(depId => {
            const dep = updatedSteps.find(s => s.id === depId);
            return dep?.status === 'completed';
          });
          if (allDepsMet) {
            updatedSteps[i] = { ...updatedSteps[i], status: 'in_progress' as const };
          }
        }
      }

      setSteps(updatedSteps);

      // Check if all steps are now completed
      const nowAllCompleted = updatedSteps.every(s => s.status === 'completed');
      if (nowAllCompleted) {
        await assignmentService.updateAssignmentStatus(assignmentId, 'completed');
      }

      // Auto-advance to next incomplete step whose dependencies are met
      const nextIndex = updatedSteps.findIndex((s, i) =>
        i !== currentStepIndex && s.status !== 'completed' &&
        s.dependsOn.every(depId => updatedSteps.find(d => d.id === depId)?.status === 'completed'),
      );
      if (nextIndex >= 0) {
        setCurrentStepIndex(nextIndex);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to complete step');
    } finally {
      setCompleting(false);
    }
  };

  const reopenStep = async () => {
    if (!assignmentId || !currentStep || currentStep.status !== 'completed') return;

    try {
      setCompleting(true);

      // Call API to reopen the step
      await assignmentService.reopenStep(assignmentId, currentStep.id);

      // Find all downstream dependents (transitive) and mark them as pending
      const downstreamIds = new Set<string>();
      const findDownstream = (stepId: string) => {
        steps.forEach(s => {
          if (s.dependsOn.includes(stepId) && !downstreamIds.has(s.id)) {
            downstreamIds.add(s.id);
            findDownstream(s.id);
          }
        });
      };
      findDownstream(currentStep.id);

      const updatedSteps = steps.map((s, i) => {
        if (i === currentStepIndex) {
          return { ...s, status: 'in_progress' as const };
        } else if (downstreamIds.has(s.id)) {
          return { ...s, status: 'pending' as const };
        }
        return s;
      });

      setSteps(updatedSteps);

      // Update assignment status back to in_progress if needed
      if (assignment?.status === 'completed') {
        await assignmentService.updateAssignmentStatus(assignmentId, 'in_progress');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to reopen step');
    } finally {
      setCompleting(false);
    }
  };

  const finishWorkflow = async () => {
    if (!assignmentId) return;

    try {
      setCompleting(true);
      await assignmentService.updateAssignmentStatus(assignmentId, 'completed');
      navigate('/assignments');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to complete workflow');
    } finally {
      setCompleting(false);
    }
  };

  const goToStep = (index: number) => {
    // Users can navigate to any step to view it
    // However, they can only complete a step if all previous steps are completed
    setCurrentStepIndex(index);
  };

  const completedCount = steps.filter(s => s.status === 'completed').length;
  const progressPercent = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;

  // Track if dashboard has unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [savingDashboard, setSavingDashboard] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>(
    'idle',
  );
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Validation state
  const [validationResult, setValidationResult] = useState<StepValidationResult | null>(null);
  const [showValidationModal, setShowValidationModal] = useState(false);

  // Callback for widget data changes (Q&A, Conviction, Image Repository, PDF Repository, Notes Repository, etc.)
  const handleDashboardDataChange = useCallback(
    (
      dataKey: string,
      newData:
        | QADataContent
        | ConvictionDataContent
        | ImageRepositoryDataContent
        | PDFRepositoryDataContent
        | NotesRepositoryDataContent
        | ScorecardDataContent
        | ExcelDataContent,
    ) => {
      console.log('[Auto-save] Data changed:', dataKey);
      setDashboardData(prev => ({
        ...prev,
        [dataKey]: newData,
      }));
      setHasUnsavedChanges(true);
    },
    [],
  );

  // Save widget data to TaskProgress (per-assignment, per-step)
  const saveDashboardData = useCallback(async () => {
    if (!assignmentId || !currentStep?.id || !hasUnsavedChanges) return true;

    try {
      setSavingDashboard(true);
      // Save to TaskProgress.data instead of Dashboard.data
      const updatedProgress = await assignmentService.updateStepProgress(
        assignmentId,
        currentStep.id,
        {
          data: dashboardData,
        },
      );
      // Update local progress map
      setProgressDataMap(prev => {
        const newMap = new Map(prev);
        newMap.set(currentStep.id, updatedProgress);
        return newMap;
      });
      setHasUnsavedChanges(false);
      return true;
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save widget data');
      return false;
    } finally {
      setSavingDashboard(false);
    }
  }, [assignmentId, currentStep?.id, dashboardData, hasUnsavedChanges]);

  // Auto-save: debounce saves after 2 seconds of no changes
  // Save widget data to TaskProgress (per-assignment, per-step) instead of Dashboard
  useEffect(() => {
    // Skip if no unsaved changes or if workflow is locked (submitted)
    if (!hasUnsavedChanges || isWorkflowLocked) return;

    // Log for debugging
    console.log('[Auto-save] Changes detected, will save in 2 seconds...', {
      assignmentId,
      stepId: currentStep?.id,
      hasUnsavedChanges,
    });

    // Clear any existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout for auto-save
    autoSaveTimeoutRef.current = setTimeout(async () => {
      // Need assignment ID and step ID to save to
      if (!assignmentId || !currentStep?.id) {
        console.log('[Auto-save] No assignment or step ID, skipping save');
        setHasUnsavedChanges(false);
        return;
      }

      try {
        setAutoSaveStatus('saving');
        console.log('[Auto-save] Saving to TaskProgress:', {
          assignmentId,
          stepId: currentStep.id,
        });
        // Save widget data to TaskProgress.data (assignment-scoped, not dashboard)
        const updatedProgress = await assignmentService.updateStepProgress(
          assignmentId,
          currentStep.id,
          {
            data: dashboardData,
          },
        );
        // Update local progress map
        setProgressDataMap(prev => {
          const newMap = new Map(prev);
          newMap.set(currentStep.id, updatedProgress);
          return newMap;
        });
        setHasUnsavedChanges(false);
        setAutoSaveStatus('saved');
        console.log('[Auto-save] Save successful');
        // Reset to idle after 2 seconds
        setTimeout(() => setAutoSaveStatus('idle'), 2000);
      } catch (err) {
        console.error('[Auto-save] Save failed:', err);
        setAutoSaveStatus('error');
      }
    }, 2000);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [assignmentId, currentStep?.id, dashboardData, hasUnsavedChanges]);

  // Handle save & close — save then navigate back to assignments
  const handleSaveAndClose = useCallback(async () => {
    const saved = await saveDashboardData();
    if (saved) {
      navigate('/assignments');
    }
  }, [saveDashboardData, navigate]);

  // Handle close without saving
  const handleCloseWithoutSaving = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowCloseConfirm(true);
    } else {
      navigate('/assignments');
    }
  }, [hasUnsavedChanges, navigate]);

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isDashboardFullscreen) {
        handleCloseWithoutSaving();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDashboardFullscreen, handleCloseWithoutSaving]);

  // Draggable toolbar handlers
  const handleToolbarDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    toolbarDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: toolbarPos.x,
      origY: toolbarPos.y,
    };

    const handleMouseMove = (ev: MouseEvent) => {
      if (!toolbarDragRef.current) return;
      const dx = ev.clientX - toolbarDragRef.current.startX;
      const dy = ev.clientY - toolbarDragRef.current.startY;
      setToolbarPos({
        x: toolbarDragRef.current.origX + dx,
        y: toolbarDragRef.current.origY + dy,
      });
    };

    const handleMouseUp = () => {
      toolbarDragRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [toolbarPos]);

  // Navigate to a different step while staying in fullscreen
  const navigateStepInFullscreen = useCallback(async (targetIndex: number) => {
    if (targetIndex < 0 || targetIndex >= steps.length) return;
    // Enforce DAG: can only navigate to a step if all its dependencies are completed
    const targetStep = steps[targetIndex];
    const isAccessible = targetStep.dependsOn.every(depId => {
      const dep = steps.find(s => s.id === depId);
      return dep?.status === 'completed';
    });
    if (!isAccessible) return;
    // Save unsaved changes before switching
    if (hasUnsavedChanges && assignmentId && currentStep?.id) {
      try {
        await assignmentService.updateStepProgress(assignmentId, currentStep.id, { data: dashboardData });
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error('Failed to save before step navigation:', err);
      }
    }
    setCurrentStepIndex(targetIndex);
    // If the target step has no dashboard, navigate back
    if (!steps[targetIndex]?.dashboardId) {
      navigate('/assignments');
    }
  }, [steps, hasUnsavedChanges, assignmentId, currentStep?.id, dashboardData, navigate]);

  // Complete current step and advance to next — stays in fullscreen
  const completeAndNext = useCallback(async () => {
    if (!assignmentId || !currentStep) return;

    // Validate all required fields before completing
    const validation = validateStepWidgets(
      dashboardData,
      dashboardWidgets,
      widgetTemplates,
      currentStep.widgetConfigs,
    );

    if (!validation.isValid) {
      setValidationResult(validation);
      setShowValidationModal(true);
      return;
    }

    try {
      setCompleting(true);

      // Save unsaved widget data first
      if (hasUnsavedChanges && currentStep.id) {
        await assignmentService.updateStepProgress(assignmentId, currentStep.id, {
          data: dashboardData,
        });
        setHasUnsavedChanges(false);
      }

      // Complete the step
      await assignmentService.completeStep(assignmentId, currentStep.id);

      // Update local state
      const updatedSteps = steps.map((s, i) =>
        i === currentStepIndex ? { ...s, status: 'completed' as const } : s,
      );

      // Activate all pending steps whose dependencies are now fully met
      for (let i = 0; i < updatedSteps.length; i++) {
        if (updatedSteps[i].status === 'pending') {
          const allDepsMet = updatedSteps[i].dependsOn.every(depId => {
            const dep = updatedSteps.find(s => s.id === depId);
            return dep?.status === 'completed';
          });
          if (allDepsMet) {
            updatedSteps[i] = { ...updatedSteps[i], status: 'in_progress' as const };
          }
        }
      }

      setSteps(updatedSteps);

      const nowAllCompleted = updatedSteps.every(s => s.status === 'completed');
      if (nowAllCompleted) {
        await assignmentService.updateAssignmentStatus(assignmentId, 'completed');
      }

      // Advance to next incomplete step whose dependencies are met
      const nextIndex = updatedSteps.findIndex((s, i) =>
        i !== currentStepIndex && s.status !== 'completed' &&
        s.dependsOn.every(depId => updatedSteps.find(d => d.id === depId)?.status === 'completed'),
      );
      if (nextIndex >= 0) {
        setCurrentStepIndex(nextIndex);
        if (!updatedSteps[nextIndex]?.dashboardId) {
          navigate('/assignments');
        }
      } else if (nowAllCompleted) {
        navigate('/assignments');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to complete step');
    } finally {
      setCompleting(false);
    }
  }, [assignmentId, currentStep, currentStepIndex, steps, dashboardData, dashboardWidgets, widgetTemplates, hasUnsavedChanges, navigate]);

  const confirmCloseWithoutSaving = useCallback(() => {
    navigate('/assignments');
  }, [navigate]);

  // Render widget content by ID (used by DashboardGrid)
  const renderWidget = useCallback(
    (widgetId: string) => {
      const widgetInstance = dashboardWidgets.find(w => w.id === widgetId);

      if (!widgetInstance) {
        return (
          <div className="h-full flex items-center justify-center text-neutral-400">
            Widget not found
          </div>
        );
      }

      const template = widgetTemplates.get(widgetInstance.templateId);

      if (!template) {
        return (
          <div className="h-full flex items-center justify-center text-neutral-400">
            Template not found
          </div>
        );
      }

      // Merge template config with instance overrides
      let mergedConfig = {
        ...template.defaultConfig,
        ...(widgetInstance.configOverrides || {}),
      } as any;

      // For dashboard_reference widgets, the config comes from dataSourceConfig
      // and the widget type should be 'dashboard_reference', not the source widget's type
      const isDashboardReference = widgetInstance.dataSourceConfig?.type === 'dashboard_reference';
      if (isDashboardReference) {
        const dsc = widgetInstance.dataSourceConfig!;
        mergedConfig = {
          ...mergedConfig,
          sourceDashboardId: dsc.sourceDashboardId,
          sourceDataKey: dsc.sourceDataKey,
          sourceWidgetType: dsc.sourceWidgetType,
          readOnly: dsc.readOnly ?? true,
          displayTitle: dsc.displayTitle || widgetInstance.title,
        };
      }

      // Widget types that require a dataKey for storing user input
      const CONFIGURABLE_WIDGET_TYPES = [
        'qa',
        'conviction',
        'image_repository',
        'pdf_repository',
        'notes_repository',
        'onedrive',
      ];

      // Apply dataKey: prefer dashboard-level configOverrides.dataKey, fall back to step-level
      const dashDk = widgetInstance.configOverrides?.dataKey;
      const hasDashDataKey = typeof dashDk === 'string' && dashDk;
      const stepConfigs = currentStep?.widgetConfigs;

      if (hasDashDataKey) {
        mergedConfig.dataKey = dashDk;
      } else if (stepConfigs?.widgets?.[widgetInstance.id]?.dataKey) {
        mergedConfig.dataKey = stepConfigs.widgets[widgetInstance.id].dataKey;
      }

      // Apply onedrive/excel config: prefer dashboard-level, fall back to step-level
      if (template.type === 'onedrive') {
        const overrides = widgetInstance.configOverrides || {};
        const stepConfig = stepConfigs?.widgets?.[widgetInstance.id];
        const fileId = (overrides.fileId as string) || stepConfig?.fileId;
        const fileName = (overrides.fileName as string) || stepConfig?.fileName;
        const fileUrl = (overrides.fileUrl as string) || stepConfig?.fileUrl;
        if (fileId) mergedConfig.fileId = fileId;
        if (fileName) mergedConfig.fileName = fileName;
        if (fileUrl) mergedConfig.fileUrl = fileUrl;
      }

      // Auto-generate dataKey for configurable widgets that don't have one
      // This ensures widgets added to dashboard after workflow was configured still work
      if (CONFIGURABLE_WIDGET_TYPES.includes(template.type) && !mergedConfig.dataKey) {
        const autoDataKey = `auto_${widgetInstance.id}`;
        mergedConfig.dataKey = autoDataKey;
      }

      // Use 'dashboard_reference' type for reference widgets, otherwise use the template type
      const widgetType = isDashboardReference ? 'dashboard_reference' : template.type;

      return (
        <div className="h-full overflow-auto relative">
          <WidgetRenderer
            config={{
              type: widgetType,
              title: widgetInstance.title || template.name,
              config: mergedConfig,
            }}
            readOnly={isWorkflowLocked}
            dashboardData={dashboardData}
            onDashboardDataChange={handleDashboardDataChange}
            assignmentId={assignmentId || undefined}
            widgetInstanceId={widgetId}
            onDataSubmit={async data => {
              // Save form data to step progress
              if (assignmentId && currentStep) {
                try {
                  await assignmentService.updateStepProgress(assignmentId, currentStep.id, {
                    data,
                  });
                } catch {
                  // Form save failed - user can retry
                }
              }
            }}
          />
        </div>
      );
    },
    [
      dashboardWidgets,
      widgetTemplates,
      dashboardData,
      handleDashboardDataChange,
      currentStep?.widgetConfigs,
    ],
  );

  // Dashboard content renderer (used in both normal and fullscreen mode)
  const renderDashboardContent = (isFullscreen: boolean = false) => {
    if (!currentStep?.dashboardId) {
      return (
        <div className="text-neutral-500 text-center py-8">
          <p className="mb-2">No dashboard configured for this step.</p>
          <p className="text-sm text-neutral-400">
            The workflow owner can add dashboards with forms, charts, and data grids in the workflow
            builder.
          </p>
        </div>
      );
    }

    if (dashboardLoading) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-3"></div>
          <p className="text-neutral-500">Loading dashboard...</p>
        </div>
      );
    }

    if (dashboardError) {
      return (
        <div className="text-center py-8 bg-danger-50 rounded-lg border border-danger-200">
          <p className="text-danger-600 mb-2">Failed to load dashboard</p>
          <p className="text-sm text-danger-500">{dashboardError}</p>
        </div>
      );
    }

    // Use DashboardGrid if we have a saved grid layout config
    // The isolate class creates a new stacking context so modals (z-[100]) can appear above
    if (gridLayoutConfig && dashboardWidgets.length > 0) {
      return (
        <div
          className="h-full relative isolate"
          style={{ zIndex: 0 }}
        >
          <DashboardGrid
            layoutConfig={gridLayoutConfig}
            widgets={dashboardWidgets}
            isEditable={false}
            hideMaximize
            renderWidget={renderWidget}
            getWidgetTitle={(wId) => {
              const w = dashboardWidgets.find(dw => dw.id === wId);
              if (!w) return 'Widget';
              const t = widgetTemplates.get(w.templateId);
              return w.title || t?.name || 'Widget';
            }}
            getWidgetIsInput={(wId) => {
              const w = dashboardWidgets.find(dw => dw.id === wId);
              if (!w) return false;
              const t = widgetTemplates.get(w.templateId);
              return t ? isInputWidget(t.type) : false;
            }}
            getWidgetRefresh={(wId) => widgetRefetchRegistry.getRefetch(wId)}
            getWidgetIsSummary={(wId) => {
              const w = dashboardWidgets.find(dw => dw.id === wId);
              return w?.isSummary || false;
            }}
            getWidgetIsShared={(wId) => {
              const w = dashboardWidgets.find(dw => dw.id === wId);
              return w?.isShared || false;
            }}
          />
        </div>
      );
    }

    // Fallback to simple grid layout if no grid layout config
    if (widgetConfigs.length > 0) {
      return (
        <div className={`${isFullscreen ? 'grid grid-cols-1 lg:grid-cols-2 gap-6' : 'space-y-6'}`}>
          {widgetConfigs.map((config, index) => (
            <div
              key={index}
              className={`border border-neutral-200 rounded-lg overflow-hidden ${isFullscreen ? 'min-h-[400px]' : ''}`}
            >
              <WidgetRenderer
                config={config}
                readOnly={isWorkflowLocked}
                dashboardData={dashboardData}
                onDashboardDataChange={handleDashboardDataChange}
                assignmentId={assignmentId || undefined}
                onDataSubmit={async data => {
                  // Save form data to step progress
                  if (assignmentId && currentStep) {
                    try {
                      await assignmentService.updateStepProgress(assignmentId, currentStep.id, {
                        data,
                      });
                    } catch {
                      // Form save failed - user can retry
                    }
                  }
                }}
              />
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="text-center py-8 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-300">
        <p className="text-neutral-500">Dashboard has no widgets configured</p>
        <p className="text-xs text-neutral-400 mt-1">
          Dashboard: {currentDashboard?.name || currentStep?.dashboardId}
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading workflow...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex items-center justify-center bg-neutral-50">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4"><FontAwesomeIcon icon={faTriangleExclamation} /></div>
          <p className="text-danger-500 mb-4">{error}</p>
          <button
            onClick={() => navigate('/assignments')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Back to Assignments
          </button>
        </div>
      </div>
    );
  }

  if (steps.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-neutral-50">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4"><FontAwesomeIcon icon={faClipboard} /></div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">No Steps Found</h2>
          <p className="text-neutral-500 mb-4">This workflow doesn't have any steps defined yet.</p>
          <button
            onClick={() => navigate('/assignments')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Back to Assignments
          </button>
        </div>
      </div>
    );
  }

  // Embed mode: render only the dashboard content, no chrome
  if (isEmbed) {
    return (
      <div className="h-full bg-surface overflow-auto">
        {dashboardLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
          </div>
        ) : dashboardError ? (
          <div className="flex items-center justify-center h-full text-neutral-500 text-sm">{dashboardError}</div>
        ) : gridLayoutConfig && dashboardWidgets.length > 0 ? (
          <div className="h-full relative isolate" style={{ zIndex: 0 }}>
            <DashboardGrid
              layoutConfig={gridLayoutConfig}
              widgets={dashboardWidgets}
              isEditable={false}
              hideMaximize
              renderWidget={renderWidget}
              getWidgetTitle={(wId) => {
                const w = dashboardWidgets.find(dw => dw.id === wId);
                if (!w) return 'Widget';
                const t = widgetTemplates.get(w.templateId);
                return w.title || t?.name || 'Widget';
              }}
              getWidgetIsInput={(wId) => {
                const w = dashboardWidgets.find(dw => dw.id === wId);
                if (!w) return false;
                const t = widgetTemplates.get(w.templateId);
                return t ? isInputWidget(t.type) : false;
              }}
              getWidgetIsSummary={(wId) => {
                const w = dashboardWidgets.find(dw => dw.id === wId);
                return w?.isSummary || false;
              }}
              getWidgetIsShared={(wId) => {
                const w = dashboardWidgets.find(dw => dw.id === wId);
                return w?.isShared || false;
              }}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-neutral-400 text-sm">No dashboard configured</div>
        )}
      </div>
    );
  }

  return (
    <div className="h-full flex bg-neutral-50">
      {/* Sidebar - Step Navigator (collapses when dashboard is expanded) */}
      <div
        className={`${isDashboardFullscreen ? 'w-0 overflow-hidden' : 'w-80'} bg-surface border-r flex flex-col transition-all duration-300`}
      >
        {/* Workflow Header */}
        <div className="p-4 border-b">
          <h2 className="text-lg font-semibold text-neutral-900 truncate">
            {workflow?.name || 'Workflow'}
          </h2>
          {workflow?.description && (
            <p className="text-sm text-neutral-500 mt-1 line-clamp-2">{workflow.description}</p>
          )}

          {/* Asset Info - shows which asset's data is being used */}
          {assignment?.asset && (
            <div className="mt-3 p-2 bg-primary-50 rounded-lg border border-primary-200">
              <div className="flex items-center gap-2">
                <span className="text-primary-600">{FALLBACK_WIDGET_ICON}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-primary-900 truncate">
                    {assignment.asset.name}
                  </p>
                  {assignment.asset.dataUid && (
                    <p className="text-xs text-primary-600">UID: {assignment.asset.dataUid}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="mt-3">
            <div className="flex justify-between text-xs text-neutral-500 mb-1">
              <span>
                {completedCount} of {steps.length} steps completed
              </span>
              <span>{progressPercent}%</span>
            </div>
            <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-success-500 transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>

        {/* All Steps List - Always Visible */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-2">
            <h3 className="px-2 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider">
              Workflow Steps
            </h3>
            <div className="space-y-1">
              {steps.map((step, index) => {
                const isSelected = index === currentStepIndex;
                // Check if all dependency steps are completed
                const canComplete = areDependenciesMet(step);

                return (
                  <button
                    key={step.id}
                    onClick={() => goToStep(index)}
                    className={`w-full text-left p-3 rounded-lg flex items-start transition-all cursor-pointer ${
                      isSelected
                        ? 'bg-primary-50 border-2 border-primary-500'
                        : step.status === 'completed'
                          ? 'bg-success-50 hover:bg-success-100 border border-transparent hover:border-success-200'
                          : step.status === 'in_progress'
                            ? 'bg-warning-50 hover:bg-warning-100 border border-transparent hover:border-warning-200'
                            : 'bg-neutral-50 hover:bg-neutral-100 border border-transparent hover:border-neutral-200'
                    }`}
                  >
                    <span
                      className={`w-7 h-7 rounded-full flex items-center justify-center mr-3 flex-shrink-0 text-sm font-medium ${
                        step.status === 'completed'
                          ? 'bg-success-500 text-white'
                          : step.status === 'in_progress'
                            ? 'bg-primary-500 text-white'
                            : 'bg-neutral-200 text-neutral-500'
                      }`}
                    >
                      {step.status === 'completed' ? <FontAwesomeIcon icon={faCheck} /> : index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <span
                        className={`block text-sm font-medium ${
                          step.status === 'pending' ? 'text-neutral-500' : 'text-neutral-900'
                        }`}
                      >
                        {step.name}
                      </span>
                      <span
                        className={`text-xs ${
                          step.status === 'completed'
                            ? 'text-success-600'
                            : step.status === 'in_progress'
                              ? 'text-primary-600'
                              : canComplete
                                ? 'text-warning-600'
                                : 'text-neutral-400'
                        }`}
                      >
                        {step.status === 'completed'
                          ? 'Completed - click to review'
                          : step.status === 'in_progress'
                            ? 'Ready to work on'
                            : canComplete
                              ? 'Ready to start'
                              : 'Complete previous steps first'}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sidebar Footer */}
        <div className="p-4 border-t space-y-2">
          {isWorkflowLocked && (
            <div className={`px-3 py-2 ${isReopenRequested ? 'bg-warning-50' : 'bg-neutral-100'} rounded-lg text-center`}>
              <span className={`text-xs ${isReopenRequested ? 'text-warning-700' : 'text-neutral-500'} font-medium`}>
                {isReopenRequested ? 'Reopen requested — awaiting approval' : 'Workflow submitted — read only'}
              </span>
            </div>
          )}
          {!isWorkflowLocked && allStepsCompleted && (
            <button
              onClick={finishWorkflow}
              disabled={completing}
              className="w-full px-4 py-2 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 text-sm font-medium"
            >
              {completing ? 'Finishing...' : <><FontAwesomeIcon icon={faCheck} /> Finish & Submit Workflow</>}
            </button>
          )}
          <button
            onClick={() => navigate('/assignments')}
            className="w-full px-4 py-2 text-neutral-600 border border-neutral-300 rounded-lg hover:bg-neutral-50 text-sm"
          >
            ← Save & Exit
          </button>
        </div>
      </div>

      {/* Main Content - Current Step (hidden when dashboard is expanded) */}
      {!isDashboardFullscreen && (
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="bg-surface border-b px-6 py-4">
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-2 text-sm text-neutral-500 mb-1">
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      currentStep?.status === 'completed'
                        ? 'bg-success-100 text-success-700'
                        : currentStep?.status === 'in_progress'
                          ? 'bg-primary-100 text-primary-700'
                          : 'bg-neutral-100 text-neutral-700'
                    }`}
                  >
                    Step {currentStepIndex + 1} of {steps.length}
                  </span>
                  {assignment?.dueDate && (
                    <span className="text-neutral-400">
                      Due: {new Date(assignment.dueDate).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <h1 className="text-2xl font-bold text-neutral-900">{currentStep?.name}</h1>
              </div>
              <div className="flex items-center gap-3">
                {/* Auto-save status indicator */}
                {autoSaveStatus === 'saving' && (
                  <span className="text-primary-600 text-xs bg-primary-50 px-2 py-1 rounded flex items-center gap-1">
                    <span className="animate-spin">↻</span> Saving...
                  </span>
                )}
                {autoSaveStatus === 'saved' && (
                  <span className="text-success-600 text-xs bg-success-50 px-2 py-1 rounded">
                    <FontAwesomeIcon icon={faCheck} /> Saved
                  </span>
                )}
                {autoSaveStatus === 'error' && (
                  <span className="text-danger-600 text-xs bg-danger-50 px-2 py-1 rounded">
                    Save failed
                  </span>
                )}
                {isWorkflowLocked && (
                  <div className="flex items-center gap-2">
                    {isReopenRequested ? (
                      <span className="text-warning-700 text-sm font-medium flex items-center gap-2 bg-warning-50 px-3 py-1.5 rounded-lg">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Reopen Requested — Awaiting Approval
                      </span>
                    ) : (
                      <>
                        <span className="text-neutral-500 text-sm font-medium flex items-center gap-2 bg-neutral-100 px-3 py-1.5 rounded-lg">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          Submitted — Read Only
                        </span>
                        <button
                          onClick={async () => {
                            if (!assignmentId) return;
                            try {
                              await assignmentService.updateAssignmentStatus(assignmentId, 'reopen_requested');
                              setAssignment(prev => prev ? { ...prev, status: 'reopen_requested' } : prev);
                            } catch (err: any) {
                              setError(err.response?.data?.detail || 'Failed to request reopen');
                            }
                          }}
                          className="px-3 py-1.5 text-sm font-medium text-warning-700 bg-warning-50 border border-warning-200 rounded-lg hover:bg-warning-100 transition-colors"
                        >
                          Request Reopen
                        </button>
                      </>
                    )}
                  </div>
                )}
                {!isWorkflowLocked && currentStep?.status === 'completed' && (
                  <>
                    <span className="text-success-600 text-sm font-medium flex items-center gap-1">
                      <FontAwesomeIcon icon={faCheck} /> Step Completed
                    </span>
                    <button
                      onClick={reopenStep}
                      disabled={completing}
                      className="px-4 py-2 text-warning-700 bg-warning-50 border border-warning-200 rounded-lg hover:bg-warning-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
                    >
                      {completing ? (
                        <>
                          <div className="w-3 h-3 border-2 border-warning-700 border-t-transparent rounded-full animate-spin"></div>
                          Reopening...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            />
                          </svg>
                          Reopen Step
                        </>
                      )}
                    </button>
                  </>
                )}
                {/* Show message if previous steps need to be completed first */}
                {!isWorkflowLocked && currentStep?.status !== 'completed' && currentStep && !areDependenciesMet(currentStep) && (
                  <span className="text-warning-600 text-sm font-medium flex items-center gap-2 bg-warning-50 px-3 py-1.5 rounded-lg">
                    <FontAwesomeIcon icon={faTriangleExclamation} /> Complete previous steps first
                  </span>
                )}
                {!isWorkflowLocked && canCompleteCurrentStep && (
                  <button
                    onClick={completeStep}
                    disabled={completing}
                    className="px-6 py-2.5 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
                  >
                    {completing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Completing...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faCheck} />
                        Mark Step Complete
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Step Content Area */}
          <div className="flex-1 overflow-auto p-6">
            <div className="max-w-4xl mx-auto">
              {/* Completed Step Banner */}
              {currentStep?.status === 'completed' && (
                <div className={`${isWorkflowLocked ? 'bg-neutral-50 border-neutral-200' : 'bg-success-50 border-success-200'} border rounded-lg p-4 mb-6 flex items-center justify-between`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl"><FontAwesomeIcon icon={faCheck} className={isWorkflowLocked ? 'text-neutral-400' : 'text-success-500'} /></span>
                    <div>
                      {isWorkflowLocked ? (
                        <>
                          <p className="font-medium text-neutral-700">
                            {isReopenRequested ? 'Reopen requested' : 'Workflow submitted'}
                          </p>
                          <p className="text-sm text-neutral-500">
                            {isReopenRequested
                              ? 'Your reopen request has been sent. The workflow will be unlocked once approved.'
                              : 'This workflow has been completed and submitted. All fields are read-only.'}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="font-medium text-success-800">This step is complete</p>
                          <p className="text-sm text-success-600">
                            Review your work or click "Reopen Step" to make changes. Reopening will mark
                            downstream steps as incomplete.
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Step Instructions Card */}
              <div className="bg-surface rounded-xl shadow-sm border border-neutral-200 p-6 mb-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                  <span className="text-primary-500"><FontAwesomeIcon icon={faClipboard} /></span>
                  Instructions
                </h3>
                {currentStep?.description ? (
                  <p className="text-neutral-600 whitespace-pre-wrap">{currentStep.description}</p>
                ) : (
                  <p className="text-neutral-400 italic">
                    No specific instructions for this step. Complete the required tasks and mark
                    this step as done when finished.
                  </p>
                )}
              </div>

              {/* Dashboard Area */}
              {currentStep?.dashboardId && (
                <div className="bg-surface rounded-xl shadow-sm border border-neutral-200 overflow-hidden mb-6">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl"><FontAwesomeIcon icon={faChartBar} /></span>
                      <div>
                        <h3 className="text-lg font-semibold text-neutral-900">
                          {currentDashboard?.name || 'Dashboard'}
                        </h3>
                        <p className="text-sm text-neutral-500">
                          {widgetConfigs.length > 0
                            ? `${widgetConfigs.length} widget${widgetConfigs.length !== 1 ? 's' : ''} configured`
                            : dashboardLoading
                              ? 'Loading...'
                              : 'No widgets configured'}
                        </p>
                      </div>
                    </div>
                    {dashboardLoading ? (
                      <div className="w-8 h-8 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                    ) : dashboardError ? (
                      <div className="text-danger-500 text-sm">{dashboardError}</div>
                    ) : (
                      <button
                        onClick={() => { setToolbarPos({ x: 0, y: 0 }); setIsDashboardFullscreen(true); }}
                        className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                          />
                        </svg>
                        Open Dashboard
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Notes/Comments Card */}
              <div className="bg-surface rounded-xl shadow-sm border border-neutral-200 p-6">
                <h3 className="text-lg font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                  <span className="text-warning-500"><FontAwesomeIcon icon={faPenToSquare} /></span>
                  Notes
                </h3>
                <textarea
                  className="w-full p-3 border border-neutral-300 rounded-lg resize-none"
                  rows={4}
                  placeholder="Add any notes or comments about this step..."
                />
                <p className="text-xs text-neutral-400 mt-2">
                  Notes are saved locally for this session.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Dashboard View — true fullscreen overlay with pinned header */}
      {isDashboardFullscreen && (
        <div className="fixed inset-0 z-40 bg-surface flex flex-col overflow-hidden">
          {/* Pinned header bar */}
          <div className="flex-shrink-0 bg-surface border-b border-neutral-200 px-4 py-1.5">
            <div className="flex items-center justify-between relative">
              {/* Left: Workflow + Asset switchers */}
              <div className="flex items-center gap-2.5 min-w-0">
                {/* Workflow: name (with switcher) */}
                <div className="relative flex-shrink-0" ref={workflowSwitcherRef}>
                  <button
                    onClick={() => { setShowWorkflowSwitcher(!showWorkflowSwitcher); setShowAssetSwitcher(false); setWorkflowSearchQuery(''); }}
                    className="flex items-center gap-1.5 text-xs hover:bg-neutral-100 cursor-pointer px-2 py-1 rounded transition-colors"
                  >
                    <span className="text-neutral-400 font-medium">Workflow:</span>
                    <span className="text-neutral-800 font-semibold truncate max-w-[160px]">{assignment?.workflow?.name || workflow?.name || 'Workflow'}</span>
                    <FontAwesomeIcon icon={faChevronDown} className={`text-[8px] text-neutral-400 transition-transform ${showWorkflowSwitcher ? 'rotate-180' : ''}`} />
                  </button>
                  {showWorkflowSwitcher && (
                    <div className="absolute top-full left-0 mt-1 w-64 bg-surface rounded-lg shadow-lg border border-neutral-200 z-50 overflow-hidden">
                      {sameAssetAssignments.length > 0 ? (
                        <>
                          <div className="p-2 border-b border-neutral-100">
                            <div className="relative">
                              <FontAwesomeIcon icon={faSearch} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 text-[10px]" />
                              <input
                                type="text"
                                value={workflowSearchQuery}
                                onChange={(e) => setWorkflowSearchQuery(e.target.value)}
                                placeholder="Search workflows..."
                                className="w-full text-xs border border-neutral-200 rounded pl-7 pr-2 py-1.5"
                                autoFocus
                              />
                            </div>
                          </div>
                          <div className="max-h-48 overflow-y-auto py-1">
                            {sameAssetAssignments
                              .filter(a => !workflowSearchQuery || a.workflow?.name.toLowerCase().includes(workflowSearchQuery.toLowerCase()))
                              .map(a => (
                                <button
                                  key={a.id}
                                  onClick={() => { setShowWorkflowSwitcher(false); navigate(`/task/${a.id}`); }}
                                  className="w-full text-left px-3 py-1.5 text-xs text-neutral-700 hover:bg-neutral-100 flex items-center justify-between gap-2"
                                >
                                  <span className="truncate">{a.workflow?.name}</span>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    a.status === 'completed' ? 'bg-success-50 text-success-600' :
                                    a.status === 'in_progress' ? 'bg-primary-50 text-primary-600' :
                                    'bg-neutral-100 text-neutral-500'
                                  }`}>
                                    {a.status === 'in_progress' ? 'in progress' : a.status}
                                  </span>
                                </button>
                              ))}
                            {sameAssetAssignments.filter(a => !workflowSearchQuery || a.workflow?.name.toLowerCase().includes(workflowSearchQuery.toLowerCase())).length === 0 && (
                              <div className="px-3 py-2 text-xs text-neutral-400">No matching workflows</div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="px-3 py-3 text-xs text-neutral-400 text-center">
                          No other workflows assigned for this asset
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Asset: name (with switcher) */}
                {assignment?.asset && (
                  <>
                    <div className="w-px h-4 bg-neutral-300 flex-shrink-0" />
                    <div className="relative flex-shrink-0" ref={assetSwitcherRef}>
                      <button
                        onClick={() => { if (sameWorkflowAssignments.length > 0) { setShowAssetSwitcher(!showAssetSwitcher); setShowWorkflowSwitcher(false); setAssetSearchQuery(''); } }}
                        className={`flex items-center gap-1.5 text-xs ${sameWorkflowAssignments.length > 0 ? 'hover:bg-primary-50 cursor-pointer' : 'cursor-default'} px-2 py-1 rounded transition-colors`}
                      >
                        <span className="text-neutral-400 font-medium">Asset:</span>
                        <span className="text-primary-700 font-semibold truncate max-w-[160px]">{assignment.asset.name}</span>
                        {sameWorkflowAssignments.length > 0 && (
                          <FontAwesomeIcon icon={faChevronDown} className="text-[8px] text-primary-400" />
                        )}
                      </button>
                      {showAssetSwitcher && sameWorkflowAssignments.length > 0 && (
                        <div className="absolute top-full left-0 mt-1 w-64 bg-surface rounded-lg shadow-lg border border-neutral-200 z-50 overflow-hidden">
                          <div className="p-2 border-b border-neutral-100">
                            <div className="relative">
                              <FontAwesomeIcon icon={faSearch} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400 text-[10px]" />
                              <input
                                type="text"
                                value={assetSearchQuery}
                                onChange={(e) => setAssetSearchQuery(e.target.value)}
                                placeholder="Search assets..."
                                className="w-full text-xs border border-neutral-200 rounded pl-7 pr-2 py-1.5"
                                autoFocus
                              />
                            </div>
                          </div>
                          <div className="max-h-48 overflow-y-auto py-1">
                            {sameWorkflowAssignments
                              .filter(a => !assetSearchQuery || a.asset?.name.toLowerCase().includes(assetSearchQuery.toLowerCase()))
                              .map(a => (
                                <button
                                  key={a.id}
                                  onClick={() => { setShowAssetSwitcher(false); navigate(`/task/${a.id}`); }}
                                  className="w-full text-left px-3 py-1.5 text-xs text-neutral-700 hover:bg-primary-50 flex items-center justify-between gap-2"
                                >
                                  <span className="truncate">{a.asset?.name}</span>
                                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                    a.status === 'completed' ? 'bg-success-50 text-success-600' :
                                    a.status === 'in_progress' ? 'bg-primary-50 text-primary-600' :
                                    'bg-neutral-100 text-neutral-500'
                                  }`}>
                                    {a.status === 'in_progress' ? 'in progress' : a.status}
                                  </span>
                                </button>
                              ))}
                            {sameWorkflowAssignments.filter(a => !assetSearchQuery || a.asset?.name.toLowerCase().includes(assetSearchQuery.toLowerCase())).length === 0 && (
                              <div className="px-3 py-2 text-xs text-neutral-400">No matching assets</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Auto-save status */}
                {autoSaveStatus === 'saving' && (
                  <span className="text-primary-600 text-[11px] bg-primary-50 px-1.5 py-0.5 rounded flex items-center gap-1 flex-shrink-0">
                    <span className="animate-spin">↻</span> Saving...
                  </span>
                )}
                {autoSaveStatus === 'saved' && (
                  <span className="text-success-600 text-[11px] bg-success-50 px-1.5 py-0.5 rounded flex-shrink-0">
                    <FontAwesomeIcon icon={faCheck} /> Saved
                  </span>
                )}
                {autoSaveStatus === 'error' && (
                  <span className="text-danger-600 text-[11px] bg-danger-50 px-1.5 py-0.5 rounded flex-shrink-0">
                    Save failed
                  </span>
                )}
              </div>

              {/* Center: Summary columns — absolutely centered so they don't shift */}
              {summaryWidgets.length > 0 && (
                <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2">
                  {summaryWidgets.map((sw) => {
                    const data = getSummaryData(sw);
                    const def = sw.widgetType ? widgetRegistry.get(sw.widgetType as WidgetType) : undefined;
                    const SummaryComponent = def?.Summary;
                    const targetStepIndex = steps.findIndex(s => s.id === sw.stepId);
                    const targetStep = targetStepIndex >= 0 ? steps[targetStepIndex] : undefined;
                    const isNavigable = targetStep && areDependenciesMet(targetStep);
                    const isCurrent = sw.stepId === currentStep?.id;

                    return (
                      <button
                        key={`${sw.stepId}-${sw.widgetId}`}
                        onClick={() => {
                          if (isNavigable && targetStepIndex >= 0) {
                            navigateStepInFullscreen(targetStepIndex);
                          }
                        }}
                        disabled={!isNavigable}
                        className={`flex items-center gap-1.5 px-3 rounded-md min-w-[100px] h-9 justify-center transition-colors ${
                          isCurrent
                            ? 'border-2 border-neutral-400 bg-neutral-100'
                            : isNavigable
                              ? 'border border-neutral-200 bg-neutral-50 hover:bg-neutral-100 hover:border-neutral-300 cursor-pointer'
                              : 'border border-neutral-200 bg-neutral-50 opacity-50 cursor-not-allowed'
                        }`}
                      >
                        <span className="text-[10px] text-neutral-400 font-medium">{sw.title}</span>
                        {SummaryComponent
                          ? <SummaryComponent data={data} config={{}} />
                          : data === undefined || data === null
                            ? <span className="text-neutral-300 text-xs">—</span>
                            : <span className="text-xs text-neutral-600">{String(data)}</span>
                        }
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Right: Step progress + Complete + Close */}
              <div className="flex items-center gap-3 flex-shrink-0">
                {/* Step progress summary — clickable to expand detail panel */}
                {steps.length > 0 && (
                  <div className="relative" ref={stepPanelRef}>
                    <button
                      onClick={() => setShowStepPanel(!showStepPanel)}
                      className="flex items-center gap-2.5 px-2.5 py-1 rounded-md hover:bg-neutral-50 transition-colors border border-transparent hover:border-neutral-200"
                    >
                      {/* Current step label */}
                      <div className="flex flex-col items-end">
                        <span className="text-[10px] text-neutral-400 leading-tight">
                          Step {currentStepIndex + 1} of {steps.length}
                        </span>
                        <span className="text-xs font-medium text-neutral-700 leading-tight truncate max-w-[140px]">
                          {currentStep?.name}
                        </span>
                      </div>
                      {/* Progress bar */}
                      <div className="flex flex-col items-center gap-0.5">
                        <div className="w-20 h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-success-500 rounded-full transition-all duration-300"
                            style={{ width: `${(steps.filter(s => s.status === 'completed').length / steps.length) * 100}%` }}
                          />
                        </div>
                        <span className="text-[9px] text-neutral-400">
                          {steps.filter(s => s.status === 'completed').length}/{steps.length} done
                        </span>
                      </div>
                      <FontAwesomeIcon icon={faChevronDown} className={`text-[8px] text-neutral-400 transition-transform ${showStepPanel ? 'rotate-180' : ''}`} />
                    </button>

                    {/* Step detail popup */}
                    {showStepPanel && (
                      <div className="absolute top-full right-0 mt-1 w-80 bg-surface rounded-lg shadow-lg border border-neutral-200 z-50 overflow-hidden">
                        {/* Popup header with progress */}
                        <div className="px-4 py-3 border-b border-neutral-100 bg-neutral-50">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-neutral-700">Workflow Progress</span>
                            <span className="text-xs text-neutral-500">
                              {steps.filter(s => s.status === 'completed').length} of {steps.length} completed
                            </span>
                          </div>
                          <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-success-500 rounded-full transition-all duration-500"
                              style={{ width: `${(steps.filter(s => s.status === 'completed').length / steps.length) * 100}%` }}
                            />
                          </div>
                        </div>

                        {/* Step list */}
                        <div className="py-1">
                          {steps.map((step, i) => {
                            const isAccessible = areDependenciesMet(step);
                            const isCurrent = i === currentStepIndex;
                            return (
                              <button
                                key={step.id}
                                onClick={() => {
                                  if (isAccessible) {
                                    navigateStepInFullscreen(i);
                                    setShowStepPanel(false);
                                  }
                                }}
                                disabled={!isAccessible}
                                className={`w-full text-left px-4 py-2 flex items-center gap-3 transition-colors ${
                                  isCurrent
                                    ? 'bg-primary-50 border-l-2 border-primary-500'
                                    : isAccessible
                                      ? 'hover:bg-neutral-50 border-l-2 border-transparent'
                                      : 'opacity-50 cursor-not-allowed border-l-2 border-transparent'
                                }`}
                              >
                                {/* Step number/status circle */}
                                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                                  step.status === 'completed'
                                    ? 'bg-success-500 text-white'
                                    : isCurrent
                                      ? 'bg-primary-600 text-white'
                                      : 'bg-neutral-200 text-neutral-500'
                                }`}>
                                  {step.status === 'completed' ? (
                                    <FontAwesomeIcon icon={faCheck} />
                                  ) : (
                                    i + 1
                                  )}
                                </div>

                                {/* Step info */}
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-neutral-800 truncate">
                                    {step.name}
                                  </div>
                                  {step.description && (
                                    <div className="text-[10px] text-neutral-400 truncate mt-0.5">
                                      {step.description}
                                    </div>
                                  )}
                                </div>

                                {/* Status badge */}
                                <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${
                                  step.status === 'completed'
                                    ? 'bg-success-50 text-success-600'
                                    : isCurrent
                                      ? 'bg-primary-50 text-primary-600'
                                      : 'bg-neutral-100 text-neutral-400'
                                }`}>
                                  {step.status === 'completed' ? 'Done' : isCurrent ? 'Current' : 'Pending'}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Divider */}
                <div className="w-px h-5 bg-neutral-200" />

                {/* Step completed badge */}
                {currentStep?.status === 'completed' && (
                  <span className="text-success-600 text-xs font-medium bg-success-50 px-2 py-0.5 rounded">
                    <FontAwesomeIcon icon={faCheck} /> Completed
                  </span>
                )}

                {/* Locked: Request Reopen or pending indicator */}
                {isWorkflowLocked && (
                  isReopenRequested ? (
                    <span className="text-warning-700 text-xs font-medium bg-warning-50 px-2 py-1 rounded">
                      Reopen Requested
                    </span>
                  ) : (
                    <button
                      onClick={async () => {
                        if (!assignmentId) return;
                        try {
                          await assignmentService.updateAssignmentStatus(assignmentId, 'reopen_requested');
                          setAssignment(prev => prev ? { ...prev, status: 'reopen_requested' } : prev);
                        } catch (err: any) {
                          setError(err.response?.data?.detail || 'Failed to request reopen');
                        }
                      }}
                      className="px-3 py-1 text-xs font-medium text-warning-700 bg-warning-50 border border-warning-200 rounded hover:bg-warning-100 transition-colors"
                    >
                      Request Reopen
                    </button>
                  )
                )}

                {/* Complete / Finish */}
                {!isWorkflowLocked && currentStep?.status !== 'completed' && (
                  <button
                    onClick={completeAndNext}
                    disabled={completing}
                    className="px-3 py-1 text-xs bg-success-600 text-white rounded hover:bg-success-700 disabled:opacity-50 font-medium"
                  >
                    {completing
                      ? 'Completing...'
                      : currentStepIndex < steps.length - 1
                        ? 'Complete'
                        : 'Finish'}
                  </button>
                )}

                {/* Close */}
                <button
                  onClick={handleCloseWithoutSaving}
                  className="px-3 py-1 text-xs text-neutral-600 bg-neutral-100 hover:bg-neutral-200 rounded font-medium"
                  title="Close (Esc)"
                >
                  Close
                </button>
              </div>
            </div>
          </div>

          {/* Dashboard Content — fills remaining viewport */}
          <div className={`flex-1 min-h-0 ${gridLayoutConfig ? 'p-2 overflow-hidden' : 'overflow-auto p-6'}`}>
            {gridLayoutConfig ? (
              renderDashboardContent(true)
            ) : (
              <div className="max-w-[1800px] mx-auto h-full">{renderDashboardContent(true)}</div>
            )}
          </div>
        </div>
      )}

      {/* Validation Error Modal */}
      {showValidationModal && validationResult && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-danger-50 px-6 py-4 border-b border-danger-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-danger-100 flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-danger-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-danger-900">Required Fields Incomplete</h3>
                  <p className="text-sm text-danger-700">{getValidationSummary(validationResult)}</p>
                </div>
              </div>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4 max-h-80 overflow-y-auto">
              <p className="text-sm text-neutral-600 mb-4">
                Please complete the following required fields before marking this step as complete:
              </p>
              <div className="space-y-4">
                {validationResult.widgets.map(widget => (
                  <div
                    key={widget.widgetId}
                    className="border border-neutral-200 rounded-lg p-4 bg-neutral-50"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-medium text-neutral-900">{widget.widgetTitle}</h4>
                    </div>
                    <ul className="space-y-1">
                      {widget.errors.map((error, idx) => (
                        <li key={idx} className="text-sm text-danger-600 flex items-start gap-2">
                          <span className="text-danger-400 mt-0.5">•</span>
                          <span>{error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t bg-neutral-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowValidationModal(false);
                  // Open dashboard if not already open
                  if (!isDashboardFullscreen && currentStep?.dashboardId) {
                    setIsDashboardFullscreen(true);
                  }
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
              >
                Open Dashboard & Complete Fields
              </button>
              <button
                onClick={() => setShowValidationModal(false)}
                className="px-4 py-2 text-neutral-600 border border-neutral-300 rounded-lg hover:bg-neutral-100 text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showCloseConfirm}
        onClose={() => setShowCloseConfirm(false)}
        onAction={confirmCloseWithoutSaving}
        title="Unsaved Changes"
        description="You have unsaved changes. Are you sure you want to close without saving?"
        actionText="Close Without Saving"
      />
    </div>
  );
};

export default TaskExecution;
